/**
 * @Author: abbeymart | Abi Akindele | @Created: 2020-07-04 | @Updated: 2020-07-04
 * @Company: mConnect.biz | @License: MIT
 * @Description: common util functions
 */

import localforage from 'localforage';
import { getResMessage, type ResponseMessage } from '@mconnect/mcresponse';
import { isEmpty } from 'lodash';
import type {
  ItemValueTypes,
  ObjectType,
  PasswordConstraintType,
  UserInfoType,
  ValidPasswordResultType,
} from 'stores/types/accessTypes';

// types
type ValueType = ObjectType | ObjectType[] | string | string[] | number | number[] | ValueObject;

interface ValueObject {
  [key: string]: ValueType;
}

interface Options {
  type?: string;
  language?: string;
}

export interface Locale<T> {
  [key: string]: T;
}

export interface MessageObjectType {
  [key: string]: string;
}

export type MessageObject = MessageObjectType | ItemValueTypes;

// export interface ResponseMessage {
//     code: string;
//     resCode: number | string;
//     message: string;
//     value: ValueType;
// }

export type ItemStateType =
  string | number | ObjectType | string[] | number[] | boolean | ObjectType[] | null;

export interface GetNamesType {
  firstname?: string;
  middlename?: string;
  lastname?: string;
}

export function isAbsoluteURL(url: string): boolean {
  if (typeof url !== 'string') return false;
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

export function getLanguage(userLang = 'en-US'): string {
  // Define/set default language variable
  let defaultLang = 'en-US';
  // Set defaultLang to current userLang, set from the UI
  if (userLang) {
    defaultLang = userLang;
  }
  return defaultLang;
}

export function getLocale<T>(localeFiles: Locale<T>, options: Options = {}): T {
  // validate localeFiles as an object
  if (typeof localeFiles !== 'object' || isEmpty(localeFiles)) {
    throw new Error('Locale files should be an object and not empty');
  }

  // const localeType = options && options.type ? options.type: "";
  const language = options && options.language ? options.language : 'en-US';

  // set the locale file contents
  return localeFiles[language] as T;
}

export function isEmptyObject(val: ObjectType): boolean {
  return typeof val === 'object'
    ? !(Object.keys(val).length > 0 && Object.values(val).length > 0)
    : false;
}

export function getParamsMessage(msgObject: MessageObject): ResponseMessage {
  if (typeof msgObject !== 'object') {
    return getResMessage('validateError', {
      message: 'Cannot process non-object-value',
    });
  }
  let messages = '';
  Object.entries(msgObject).forEach(([key, msg]) => {
    messages = messages ? `${messages} | ${key} : ${msg}` : `${key} : ${msg}`;
  });
  return getResMessage('validateError', {
    message: messages,
  });
}

export function shortString(str: string, maxLength = 20): string {
  return str.toString().length > maxLength
    ? str.toString().substring(0, maxLength) + '...'
    : str.toString();
}

export function strToBool(val: string | number = 'n'): boolean {
  const strVal = val.toString().toLowerCase();
  if (strVal === 'true' || strVal === 't' || strVal === 'yes' || strVal === 'y') {
    return true;
  } else {
    return Number(strVal) > 0;
  }
}

export async function userIpInfo(
  ipUrl = 'https://ipinfo.io',
  options: ObjectType = {},
): Promise<ObjectType> {
  // Get the current user IP address Information
  // TODO: use other method besides ipinfo.io, due to query limit/constraint (i.e. 429 error)
  try {
    // const reqH = options && options.headers? options. headers : {};
    const reqHeaders = { 'Content-Type': 'application/json' };
    options = Object.assign({}, options, {
      method: 'GET',
      mode: 'cors',
      headers: reqHeaders,
    });
    const response = await fetch(ipUrl, options);
    let result = await response.json();
    result = result ? JSON.parse(result) : null;
    if (response.ok) {
      return result;
    }
    // noinspection ExceptionCaughtLocallyJS
    throw new Error('Error fetching ip-address information: ');
  } catch (error) {
    console.log('Error fetching ip-address information: ', error);
    throw new Error(error.message);
  }
}

export function userBrowser() {
  // push each browser property, as key/value pair, into userBrowser array variable
  return navigator.userAgent;
}

export function currentUrlInfo(pathLoc: string) {
  // remove query section
  pathLoc = pathLoc.split('?')[0] || '';
  // this function returns the parts (array) and lastIndex of a URL/pathLocation
  let parts: string[] = [];
  let lastIndex = -1;
  if (pathLoc) {
    parts = pathLoc.toString().split('://')[1]?.split('/') || [];
    // get the last index
    lastIndex =
      parts.lastIndexOf('new') || parts.lastIndexOf('detail') || parts.lastIndexOf('list');
    return {
      parts,
      lastIndex,
    };
  }
  return {
    parts,
    lastIndex,
  };
}

export function getPath(req: Request): string {
  let itemPath = req.url || '/mc';
  itemPath = itemPath.split('/')[1] || '';
  return itemPath ? itemPath : 'mc';
}

export function getFullName(firstName: string, lastName: string, middleName = ''): string {
  if (firstName && middleName && lastName) {
    return firstName + ' ' + middleName + ' ' + lastName;
  }
  return firstName + ' ' + lastName;
}

export function getNames(fullName: string): GetNamesType {
  const nameParts = fullName.split('');
  let firstname = '',
    lastname = '',
    middlename = '';
  if (nameParts.length > 2) {
    firstname = nameParts[0] || '';
    lastname = nameParts[2] || '';
    middlename = nameParts[1] || '';
    return {
      firstname,
      middlename,
      lastname,
    };
  } else {
    firstname = nameParts[0] || '';
    lastname = nameParts[1] || '';
    return {
      firstname,
      lastname,
    };
  }
  // Return firstName, middleName and lastName based on fullName components ([0], [1], [2])
}

export function pluralize(n: number, itemName: string, itemPlural = ''): string {
  // @TODO: retrieve plural for itemName from language dictionary {name: plural}
  let itemNamePlural: string;
  if (!itemPlural) {
    itemNamePlural = 'tbd';
    // itemNamePlural = mcPlurals[ itemName ];
  } else {
    itemNamePlural = itemPlural;
  }
  let result = `${n} ${itemName}`;
  if (n > 1) {
    result = `${n} ${itemName}${itemNamePlural}`;
  }
  return result;
}

// Validation functions
export function isProvided(param: string | number | ObjectType): boolean {
  // Verify the Required status
  // Validate that the item is not empty / null / undefined
  return !(param === '' || param === null || param === undefined || isEmpty(param));
}

export function isEven(num: number): boolean {
  return Number.isFinite(num) && num % 2 === 0;
}

export function isOdd(num: number): boolean {
  return Number.isFinite(num) && num % 2 !== 0;
}

export function isNumberDigit(num: number): boolean {
  // Validate that param is a number (digit): 100 | 99 | 33 | 44 | 200
  const numberPattern = /^\d+$/;
  return numberPattern.test(num.toString());
}

export function isNumberFloat(num: number): boolean {
  // Validate that param is a number (float): 0.90 | 99.9 | 33.3 | 44.40
  const numberPattern = /^(\d)+([.])?(\d)*$/;
  return numberPattern.test(num.toString());
}

export function isObjectType(param: ObjectType): boolean {
  // Validate param is an object, {}
  return typeof param === 'object' && !Array.isArray(param);
}

export function isArrayType(param: []): boolean {
  // Validate param is an object, []
  return Array.isArray(param);
}

export function isStringChar(param: string): boolean {
  // Validate that param is a string (characters only) -- use regEx
  const charRegEx = /^[a-zA-Z&$_-]+$/;
  return charRegEx.test(param);
}

export function isStringAlpha(param: string): boolean {
  // Validate that param is a string (alphanumeric, chars/numbers only)
  const alphaNumericPattern = /^[a-zA-Z\d-_]+$/;
  return alphaNumericPattern.test(param);
}

export function isUsername(param: string): boolean {
  const usernamePattern = /^([a-zA-Z\d_])+$/; // alphanumeric, underscore, no space
  return usernamePattern.test(param);
}

export function isNull(infoItem: null): boolean {
  return infoItem === null;
}

export function isEmail(param: string): boolean {
  const testPattern =
    /^[\da-zA-Z]+([\da-zA-Z]*[-._+])*[\da-zA-Z]+@[\da-zA-Z]+([-.][\da-zA-Z]+)*([\da-zA-Z]*[.])[a-zA-Z]{2,6}$/;
  // const testPattern = /^[\da-zA-Z]+([\-._][\da-zA-Z]+)*@[\da-zA-Z]+([\-.][\da-zA-Z]+)*([.])[a-zA-Z]{2,6}$/;
  return testPattern.test(param);
}

export function isPassword(param: string): boolean {
  const testPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*)(?=.*\W.*)[a-zA-Z\d\S]{6,15}$/;
  return testPattern.test(param);
}

export function isNumberOnRange(num: number, min: number, max: number): boolean {
  if ((isNumberDigit(num) || isNumberFloat(num)) && min < max) {
    return num >= min && num <= max;
  }
  return false;
}

export function isPhone(param: string): boolean {
  const phonePattern = /^([1-9]{1,3})?[-. ]?(\(\d{3}\)?[-. ]?|\d{3}?[-. ]?)?\d{3}?[-. ]?\d{4}$/;
  return phonePattern.test(param);
}

export function isPostalCode(param: string): boolean {
  const postCodePattern = /^[a-zA-Z\d]+(\s)?[a-zA-Z\d]*/;
  return postCodePattern.test(param);
}

export function isPostalCodeUS(param: string): boolean {
  const postCodePattern = /^[a-zA-Z\d]+(\s)?[a-zA-Z\d]*/;
  return postCodePattern.test(param);
}

export function isPostalCodeCanada(param: string): boolean {
  const postCodePattern = /^[a-zA-Z\d]+(\s)?[a-zA-Z\d]*/;
  return postCodePattern.test(param);
}

export function isPostalCodeUK(param: string): boolean {
  const postCodePattern = /^[a-zA-Z\d]+(\s)?[a-zA-Z\d]*/;
  return postCodePattern.test(param);
}

export function isName(param: string): boolean {
  const namePattern = /^[a-zA-Z"'-]+(\s[a-zA-Z"'-])*[a-zA-Z"'-]*/; // Abi Charles Africa America
  return namePattern.test(param);
}

export function isGenericName(param: string): boolean {
  const namePattern = /^[a-zA-Z0-9"'/:+-]+(\s[a-zA-Z0-9"':+-])*[a-zA-Z0-9"':+-]*/; // Abi Charles Africa America
  return namePattern.test(param);
}

export function isURL(param: string): boolean {
  // Abi Charles Africa America
  // const namePattern = /^[a-zA-Z\d\-\\/_.:]+$/;
  const namePattern =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
  return namePattern.test(param);
}

export function isBusinessNumber(param: string): boolean {
  // business number format
  const bnPattern = /^[\d-]+$/;
  return bnPattern.test(param);
}

export function isStandardCode(param: string): boolean {
  // Product Group | Body & Soul10
  const standardCodePattern = /^[a-zA-Z\d]+[&\s\-_]*[a-zA-Z\d$#]*$/;
  return standardCodePattern.test(param);
}

export function isCountryCode(param: string): boolean {
  // langCode must be string of format en-US
  const countryCodePattern = /^[a-z]{2}-[A-Z]{2}$/;
  return countryCodePattern.test(param);
}

export function isLanguageCode(param: string): boolean {
  // langCode must be string of format en-US
  const langCodePattern = /^[a-z]{2}-[A-Z]{2}$/;
  return langCodePattern.test(param);
}

export function isWordSpace(param: string): boolean {
  // words with spaces and hyphens, no numbers
  const wordSpacePattern = /^[a-zA-Z\d,()"'._&]+[\s\-a-zA-Z\d,()"'._&]*[a-zA-Z\d,()"'._?]*$/;
  return wordSpacePattern.test(param);
}

export function isLeapYear(year: number): boolean {
  // Any year that is evenly divisible by 400
  // OR
  // Any year that is evenly divisible by 4 but not evenly divisible by 100
  // OR
  // the day of the month February for the specified year is 29
  return (
    year % 400 === 0 ||
    (year % 4 === 0 && year % 100 !== 0) ||
    new Date(year, 1, 29).getDate() === 29
  );
}

export function isLabelCode(param: string): boolean {
  // firstName_middleName_lastName
  const labelCodePattern = /^[a-zA-Z\d]+[_\-a-zA-Z-\d]*[_a-z\d]*$/;
  return labelCodePattern.test(param);
}

export function isErrorCode(param: string): boolean {
  // error code format (AB10-100, AB900)
  const errorCodePattern = /^[a-zA-Z\d]+-*\d*$/;
  return errorCodePattern.test(param);
}

export function isPathName(param: string): boolean {
  // mysite.new_base.nicelook
  const pathNamePattern = /^[a-zA-Z\d/]+[_a-zA-Z\d./]*[a-zA-Z\d/]*$/;
  return pathNamePattern.test(param);
}

export function isNameNoSpace(param: string): boolean {
  // JohnPaul
  const nameNoSpacePattern = /[a-zA-Z]+/;
  return nameNoSpacePattern.test(param);
}

export function isDescription(param: string): boolean {
  const descPattern = /^[a-zA-Z-\d\s\\.,:=>/()*_|\-!@#$%&']+$/; // Alphanumeric string with spaces, and
  // (.,:/()*_-|!@)
  return descPattern.test(param);
}

export function isMessage(param: string): boolean {
  const descPattern = /^[a-zA-Z-\d\s\\.,:/()*_\-!@#$%&'-]+$/; // Alphanumeric string with spaces, and
  // (.,:/()*_-!@) - exclude |, reserved for message separation
  return descPattern.test(param);
}

export function isCurrency(param: string): boolean {
  const currencyPattern = /^[a-zA-Z#$]+$/;
  return currencyPattern.test(param);
}

export function getAge(dateString: string): number {
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && birthDate.getDate() > today.getDate())) {
    age--;
  }
  return age;
}

export function isJsonObject(strData: string): boolean {
  try {
    JSON.parse(strData);
  } catch {
    return false;
  }
  return true;
}

export const localForageUtils = {
  // Web store functions:
  setCookie(cname: string, cvalue: string, exdays: number) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = `${cname}=${cvalue}; ${expires}; path=/`;
  },
  getCookie(cname: string): string {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      // trim white/empty spaces
      const c = ca[i]?.trimStart();
      // if cookie exists, return the value
      if (c?.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  },
  checkCookie() {
    let username: string | null = this.getCookie('username') || '';
    if (username !== '') {
      //TODO: perform action with set value;
    } else {
      username = prompt('Please enter your name:', '');
      if (username !== '' && username !== null) {
        this.setCookie('username', username, 365);
      }
    }
  },
  mcStore(options = { storageName: '' }) {
    // localforage instance for client/UI only
    const storageName = options && options.storageName ? options.storageName : 'mconnectStore';
    return localforage.createInstance({ name: storageName });
  },
  mcStoreTest(options = { storageName: '' }) {
    // NOTE: *****this method is strictly for testing only*****
    // localforage instance for client/UI only
    return options && options.storageName && typeof options.storageName
      ? options.storageName
      : 'mconnectStore';
  },
  async setItemState(itemKey: string, itemValue: ItemStateType, expire: number) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem(itemKey, itemValue);
      await mStore.setItem(`${itemKey}Expire`, expire);
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  async setItemStateNoExpiry(itemKey: string, itemValue: ItemStateType) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem(itemKey, itemValue);
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  async removeItemState(itemKey: string) {
    try {
      const mStore = this.mcStore();
      await mStore.removeItem(itemKey);
      await mStore.removeItem(`${itemKey}Expire`);
    } catch (e) {
      console.error('error removing localforage item: ', e.stack);
    }
  },
  async getItemState(itemKey: string): Promise<ItemStateType> {
    try {
      const mStore = this.mcStore();
      const item: ItemStateType = (await mStore.getItem(itemKey)) || '',
        expire = await mStore.getItem(`${itemKey}Expire`);
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        await this.removeItemState(itemKey);
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  async getItemStateNoExpiry(itemKey: string): Promise<ItemStateType> {
    try {
      const mStore = this.mcStore();
      return (await mStore.getItem(itemKey)) || '';
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  async setToken(token: string, expire: number) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem('authToken', token);
      await mStore.setItem('authTokenExpire', expire);
    } catch (e) {
      console.error('error setting/saving localStorage item (setToken):', e.message);
    }
  },
  async removeToken() {
    try {
      const mStore = this.mcStore();
      await mStore.removeItem('authToken');
      await mStore.removeItem('authTokenExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeToken): ', e.message);
    }
  },
  async getToken(): Promise<string> {
    try {
      const mStore = this.mcStore();
      const item: string = (await mStore.getItem('authToken')) || '',
        expire = await mStore.getItem('authTokenExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        // await removeItemState(itemKey);
        await this.removeToken();
        await this.removeCurrentUser();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localStorage item (getToken): ', e.message);
      return '';
    }
  },
  async loggedIn(): Promise<boolean> {
    try {
      return !!(await this.getToken());
    } catch (e) {
      console.error('error getting localStorage item (loggedIn): ', e.message);
      return false;
    }
  },
  async setLoginName(name: string, expire: number) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem('loginName', name);
      await mStore.setItem('loginNameExpire', expire);
    } catch (e) {
      console.error('error setting/saving localStorage item (setLoginName):', e.message);
    }
  },
  async removeLoginName() {
    try {
      const mStore = this.mcStore();
      await mStore.removeItem('loginName');
      await mStore.removeItem('loginNameExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeLoginName): ', e.message);
    }
  },
  async getLoginName(): Promise<string> {
    try {
      const mStore = this.mcStore();
      const item: string = (await mStore.getItem('loginName')) || '',
        expire = await mStore.getItem('loginNameExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        await this.removeLoginName();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error retrieving localStorage item(getLoginName): ', e.message);
      return '';
    }
  },
  async setCurrentUser(userInfo: UserInfoType) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem('currentUser', userInfo);
    } catch (e) {
      console.error('error setting localStorage item(setCurrentUser): ', e.message);
    }
  },
  async removeCurrentUser() {
    try {
      const mStore = this.mcStore();
      await mStore.removeItem('currentUser');
    } catch (e) {
      console.error('error removing localStorage item(removeCurrentUser): ', e.message);
    }
  },
  async getCurrentUser(): Promise<UserInfoType> {
    try {
      const mStore = this.mcStore();
      const item: UserInfoType = (await mStore.getItem('currentUser')) || {};
      return item ? item : {};
    } catch (e) {
      console.error('error retrieving localStorage item(getCurrentUser): ', e.message);
      return {};
    }
  },
  async setApiToken(token: string) {
    try {
      const mStore = this.mcStore();
      await mStore.setItem('apiToken', token);
    } catch (e) {
      console.error('error setting localStorage item(setApiToken): ', e.message);
    }
  },
  async removeApiToken() {
    try {
      const mStore = this.mcStore();
      await mStore.removeItem('apiToken');
    } catch (e) {
      console.error('error removing localStorage item(removeApiToken): ', e.message);
    }
  },
  async getApiToken(): Promise<string> {
    try {
      const mStore = this.mcStore();
      const item: string = (await mStore.getItem('apiToken')) || '';
      return item ? item : '';
    } catch (e) {
      console.error('error retrieving localStorage item(getApiToken): ', e.message);
      return '';
    }
  },
};

export const localStorageUtils = {
  // Web store functions:
  setCookie(cname: string, cvalue: string, exdays: number) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = `${cname}=${cvalue}; ${expires}; path=/`;
  },
  getCookie(cname: string): string {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      // trim white/empty spaces
      const c = ca[i]?.trimStart();
      // if cookie exists, return the value
      if (c?.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  },
  checkCookie() {
    let username: string | null = this.getCookie('username') || '';
    if (username !== '') {
      //TODO: perform action with set value;
    } else {
      username = prompt('Please enter your name:', '');
      if (username !== '' && username !== null) {
        this.setCookie('username', username, 365);
      }
    }
  },
  setItemState(itemKey: string, itemValue: ItemStateType, expire: number) {
    try {
      window.localStorage.setItem(itemKey, JSON.stringify(itemValue));
      window.localStorage.setItem(`${itemKey}Expire`, expire.toString());
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  setItemStateNoExpiry(itemKey: string, itemValue: ItemStateType) {
    try {
      window.localStorage.setItem(itemKey, JSON.stringify(itemValue));
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  removeItemState(itemKey: string) {
    try {
      window.localStorage.removeItem(itemKey);
      window.localStorage.removeItem(`${itemKey}Expire`);
    } catch (e) {
      console.error('error removing localforage item: ', e.stack);
    }
  },
  getItemState(itemKey: string): ItemStateType {
    try {
      const lstoreValue = window.localStorage.getItem(itemKey) || '';
      const item: ItemStateType = isJsonObject(lstoreValue) ? JSON.parse(lstoreValue) : lstoreValue,
        expire = window.localStorage.getItem(`${itemKey}Expire`);
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        this.removeItemState(itemKey);
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  getItemStateNoExpiry(itemKey: string): ItemStateType {
    try {
      const lstoreValue = window.localStorage.getItem(itemKey) || '';
      return isJsonObject(lstoreValue) ? JSON.parse(lstoreValue) : lstoreValue;
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  setToken(token: string, expire: number) {
    try {
      window.localStorage.setItem('authToken', token);
      window.localStorage.setItem('authTokenExpire', JSON.stringify(expire));
    } catch (e) {
      console.error('error setting/saving localStorage item (setToken):', e.message);
    }
  },
  removeToken() {
    try {
      window.localStorage.removeItem('authToken');
      window.localStorage.removeItem('authTokenExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeToken): ', e.message);
    }
  },
  getToken(): string {
    try {
      const item: string = window.localStorage.getItem('authToken') || '',
        expire = window.localStorage.getItem('authTokenExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        // await removeItemState(itemKey);
        this.removeToken();
        this.removeCurrentUser();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localStorage item (getToken): ', e.message);
      return '';
    }
  },
  loggedIn(): boolean {
    try {
      return !!this.getToken();
    } catch (e) {
      console.error('error getting localStorage item (loggedIn): ', e.message);
      return false;
    }
  },
  setLoginName(name: string, expire: number) {
    try {
      window.localStorage.setItem('loginName', name);
      window.localStorage.setItem('loginNameExpire', JSON.stringify(expire));
    } catch (e) {
      console.error('error setting/saving localStorage item (setLoginName):', e.message);
    }
  },
  removeLoginName() {
    try {
      window.localStorage.removeItem('loginName');
      window.localStorage.removeItem('loginNameExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeLoginName): ', e.message);
    }
  },
  getLoginName(): string {
    try {
      const item = window.localStorage.getItem('loginName') || '',
        expire = window.localStorage.getItem('loginNameExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        this.removeLoginName();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error retrieving localStorage item(getLoginName): ', e.message);
      return '';
    }
  },
  setCurrentUser(userInfo: UserInfoType) {
    try {
      window.localStorage.setItem('currentUser', JSON.stringify(userInfo));
    } catch (e) {
      console.error('error setting localStorage item(setCurrentUser): ', e.message);
    }
  },
  removeCurrentUser() {
    try {
      window.localStorage.removeItem('currentUser');
    } catch (e) {
      console.error('error removing localStorage item(removeCurrentUser): ', e.message);
    }
  },
  getCurrentUser(): UserInfoType {
    try {
      const item: UserInfoType = JSON.parse(window.localStorage.getItem('currentUser') || '') || {};
      return item ? item : {};
    } catch (e) {
      console.error('error retrieving localStorage item(getCurrentUser): ', e.message);
      return {};
    }
  },
  setApiToken(token: string) {
    try {
      window.localStorage.setItem('apiToken', token);
    } catch (e) {
      console.error('error setting localStorage item(setApiToken): ', e.message);
    }
  },
  removeApiToken() {
    try {
      window.localStorage.removeItem('apiToken');
    } catch (e) {
      console.error('error removing localStorage item(removeApiToken): ', e.message);
    }
  },
  getApiToken(): string {
    try {
      return window.localStorage.getItem('apiToken') || '';
    } catch (e) {
      console.error('error retrieving localStorage item(getApiToken): ', e.message);
      return '';
    }
  },
};

export const localStorageUtils2 = {
  // Web store functions:
  setCookie(cname: string, cvalue: string, exdays: number) {
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = `${cname}=${cvalue}; ${expires}; path=/`;
  },
  getCookie(cname: string): string {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      // trim white/empty spaces
      const c = ca[i]?.trimStart();
      // if cookie exists, return the value
      if (c?.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  },
  checkCookie() {
    let username: string | null = this.getCookie('username') || '';
    if (username !== '') {
      //TODO: perform action with set value;
    } else {
      username = prompt('Please enter your name:', '');
      if (username !== '' && username !== null) {
        this.setCookie('username', username, 365);
      }
    }
  },
  setItemState(itemKey: string, itemValue: ItemStateType, expire: number) {
    try {
      window.localStorage.setItem(itemKey, JSON.stringify(itemValue));
      window.localStorage.setItem(`${itemKey}Expire`, expire.toString());
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  setItemStateNoExpiry(itemKey: string, itemValue: ItemStateType) {
    try {
      window.localStorage.setItem(itemKey, JSON.stringify(itemValue));
    } catch (e) {
      console.error('error setting/saving localforage item: ', e.stack);
    }
  },
  removeItemState(itemKey: string) {
    try {
      window.localStorage.removeItem(itemKey);
      window.localStorage.removeItem(`${itemKey}Expire`);
    } catch (e) {
      console.error('error removing localforage item: ', e.stack);
    }
  },
  getItemState(itemKey: string): ItemStateType {
    try {
      const lstoreValue = window.localStorage.getItem(itemKey) || '';
      const item: ItemStateType = isJsonObject(lstoreValue) ? JSON.parse(lstoreValue) : lstoreValue,
        expire = window.localStorage.getItem(`${itemKey}Expire`);
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        this.removeItemState(itemKey);
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  getItemStateNoExpiry(itemKey: string): ItemStateType {
    try {
      const lstoreValue = window.localStorage.getItem(itemKey) || '';
      return isJsonObject(lstoreValue) ? JSON.parse(lstoreValue) : lstoreValue;
    } catch (e) {
      console.error('error getting localforage data: ', e.stack);
      return '';
    }
  },
  setToken(token: string, expire: number) {
    try {
      window.localStorage.setItem('authToken', token);
      window.localStorage.setItem('authTokenExpire', JSON.stringify(expire));
    } catch (e) {
      console.error('error setting/saving localStorage item (setToken):', e.message);
    }
  },
  removeToken() {
    try {
      window.localStorage.removeItem('authToken');
      window.localStorage.removeItem('authTokenExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeToken): ', e.message);
    }
  },
  getToken(): string {
    try {
      const item: string = window.localStorage.getItem('authToken') || '',
        expire = window.localStorage.getItem('authTokenExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        // await removeItemState(itemKey);
        this.removeToken();
        this.removeCurrentUser();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error getting localStorage item (getToken): ', e.message);
      return '';
    }
  },
  loggedIn(): boolean {
    try {
      return !!this.getToken();
    } catch (e) {
      console.error('error getting localStorage item (loggedIn): ', e.message);
      return false;
    }
  },
  setLoginName(name: string, expire: number) {
    try {
      window.localStorage.setItem('loginName', name);
      window.localStorage.setItem('loginNameExpire', JSON.stringify(expire));
    } catch (e) {
      console.error('error setting/saving localStorage item (setLoginName):', e.message);
    }
  },
  removeLoginName() {
    try {
      window.localStorage.removeItem('loginName');
      window.localStorage.removeItem('loginNameExpire');
    } catch (e) {
      console.error('error removing localStorage item(removeLoginName): ', e.message);
    }
  },
  getLoginName(): string {
    try {
      const item = window.localStorage.getItem('loginName') || '',
        expire = window.localStorage.getItem('loginNameExpire');
      if (!item || !expire) {
        return '';
      }
      if (Date.now() > Number(expire)) {
        this.removeLoginName();
        return '';
      }
      return item;
    } catch (e) {
      console.error('error retrieving localStorage item(getLoginName): ', e.message);
      return '';
    }
  },
  setCurrentUser(userInfo: UserInfoType) {
    try {
      window.localStorage.setItem('currentUser', JSON.stringify(userInfo));
    } catch (e) {
      console.error('error setting localStorage item(setCurrentUser): ', e.message);
    }
  },
  removeCurrentUser() {
    try {
      window.localStorage.removeItem('currentUser');
    } catch (e) {
      console.error('error removing localStorage item(removeCurrentUser): ', e.message);
    }
  },
  getCurrentUser(): UserInfoType {
    try {
      const item: UserInfoType = JSON.parse(window.localStorage.getItem('currentUser') || '') || {};
      return item ? item : {};
    } catch (e) {
      console.error('error retrieving localStorage item(getCurrentUser): ', e.message);
      return {};
    }
  },
  setApiToken(token: string) {
    try {
      window.localStorage.setItem('apiToken', token);
    } catch (e) {
      console.error('error setting localStorage item(setApiToken): ', e.message);
    }
  },
  removeApiToken() {
    try {
      window.localStorage.removeItem('apiToken');
    } catch (e) {
      console.error('error removing localStorage item(removeApiToken): ', e.message);
    }
  },
  getApiToken(): string {
    try {
      return window.localStorage.getItem('apiToken') || '';
    } catch (e) {
      console.error('error retrieving localStorage item(getApiToken): ', e.message);
      return '';
    }
  },
};

// IsValidPassword function validates a given password based on the customized password specifications
export const isValidPassword = (
  param: string,
  options: PasswordConstraintType,
): ValidPasswordResultType => {
  const minLen = options.minLength || 6;
  const maxLen = options.maxLength || 100;
  const specialStr = options.includeSpecialString || '';
  const numStr = options.includeNumber || '';
  const specialStrCount = options.includeSpecialStringCount || 0;
  const numCount = options.includeNumberCount || 0;
  const uCaseStrCount = options.upperCaseStringCount || 0;

  const msg = `Password length must be a minimum of ${minLen}, maximum of ${maxLen}.
   It should contain at least ${specialStrCount} special character(s)[${specialStr}],
    ${numCount} number[${numStr}] and ${uCaseStrCount} upper-case character(s)`;

  // validate password
  const pwdSlice = param.split('');
  const strSlice = specialStr?.split('') || [];
  const numSlice = numStr.split('') || [];
  const pwdLen = pwdSlice?.length || 0;

  // password-length-validation
  if (pwdLen < minLen || pwdLen > maxLen) {
    return {
      valid: false,
      message: msg,
    };
  }
  // password-case-validations
  let uCaseCtn = 0;
  for (const pVal of param) {
    // const regExp = /[A-Z]/; || /\\p{Lu}/u
    // regEx for broader Unicode support
    const regExp = /\p{Lu}/u;
    if (regExp.test(pVal) || pVal === pVal.toUpperCase()) {
      uCaseCtn++;
    }
  }

  if (uCaseCtn < uCaseStrCount) {
    return {
      valid: false,
      message: msg,
    };
  }

  // password-special-string-validation
  let sStrCtn = 0;
  for (const sVal of strSlice) {
    if (param.includes(sVal)) {
      sStrCtn += 1;
    }
  }
  if (sStrCtn < specialStrCount) {
    return {
      valid: false,
      message: msg,
    };
  }

  // password-number-validation
  let nStrCtn = 0;
  for (const nVal of numSlice) {
    if (param.includes(nVal)) {
      nStrCtn++;
    }
  }

  if (nStrCtn < numCount) {
    return {
      valid: false,
      message: msg,
    };
  }

  return {
    valid: true,
    message: 'Passed',
  };
};
